import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Dropdown, Menu, Modal, Typography } from 'antd';
import { DownOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
// API
import { GET_AGENCIES } from 'api/campaignV2/queries';
// Types
import {
  GetAgencies,
  GetAgencies_getAgencies_entities,
  GetAgenciesVariables,
} from 'api/campaignV2/types/GetAgencies';
import { AgencyListOrderBy, SortDirection } from 'api/graphql-global-types';
// Constants
import { BRAND_DETAILS, BRAND_TALENT_CRM } from 'constants/routes';
import { DEFAULT_CURRENT_PAGE } from 'ui/Table';
// Hooks
import { useLoginAs } from 'hooks';
// UI
import Table from 'ui/Table';
import { SortedInfo, TableFilter } from 'ui/Table';
// Components
import EditBrandModal from './components/EditBrandModal';

const { Title } = Typography;

const BrandsTableComponent: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { itemsPerPage, pageNumber } = useParams<{
    itemsPerPage?: string;
    pageNumber?: string;
  }>();

  const [pageSize, setPageSize] = useState<number>(Number(itemsPerPage || 10));
  const [currentPage, setCurrentPage] = useState<number>(
    Number(pageNumber || DEFAULT_CURRENT_PAGE)
  );

  const handleSetPagination = useCallback(
    (page: number, size: number): void => {
      const basePath = '/brands';
      history.push(`${basePath}/${size}/${page}${location.search}`);
      setCurrentPage(page);
      setPageSize(size);
    },
    [history, location.search]
  );

  // Sync state when URL params change
  useEffect(() => {
    if (itemsPerPage && Number(itemsPerPage) !== pageSize) {
      setPageSize(Number(itemsPerPage));
    }
  }, [itemsPerPage, pageSize]);

  useEffect(() => {
    if (pageNumber && Number(pageNumber) !== currentPage) {
      setCurrentPage(Number(pageNumber));
    }
  }, [pageNumber, currentPage]);

  const [sort, setSortInfo] = useState<SortedInfo<AgencyListOrderBy>>(() => {
    const searchParams = new URLSearchParams(location.search);
    const sortBy = searchParams.get('sortBy') || AgencyListOrderBy.firstName;
    const sortOrder =
      (searchParams.get('sortOrder') as SortDirection) || SortDirection.ASC;
    return {
      order: sortOrder,
      key: sortBy as AgencyListOrderBy,
    };
  });

  const [searchFilters, setSearchFilters] = useState<TableFilter[]>(() => {
    const searchParams = new URLSearchParams(location.search);
    const filters: TableFilter[] = [];

    searchParams.forEach((value, key) => {
      if (!['page', 'pageSize', 'sortBy', 'sortOrder'].includes(key)) {
        const parsedValue = value.includes(',') ? value.split(',') : value;
        filters.push({ key, value: parsedValue });
      }
    });

    return filters;
  });

  // Redirect to pagination path if on base /brands route
  useEffect(() => {
    if (!itemsPerPage || !pageNumber) {
      const newPath = `/brands/${pageSize}/${currentPage}${location.search}`;
      if (location.pathname !== newPath) {
        history.replace(newPath);
      }
    }
  }, [
    itemsPerPage,
    pageNumber,
    pageSize,
    currentPage,
    location.pathname,
    location.search,
    history,
  ]);

  // Track if we're updating from URL (to prevent infinite loops)
  const isUpdatingFromUrl = React.useRef(false);
  const prevSearchRef = React.useRef(location.search);

  // Sync state from URL query params when they change (e.g., browser back button)
  useEffect(() => {
    if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
      prevSearchRef.current = location.search;
      return;
    }

    if (location.search === prevSearchRef.current) {
      return;
    }

    prevSearchRef.current = location.search;

    const searchParams = new URLSearchParams(location.search);
    const sortByParam = searchParams.get('sortBy');
    const sortOrderParam = searchParams.get('sortOrder');

    if (sortByParam || sortOrderParam) {
      const newSortBy = sortByParam || AgencyListOrderBy.firstName;
      const newSortOrder =
        (sortOrderParam as SortDirection) || SortDirection.ASC;
      if (newSortBy !== sort.key || newSortOrder !== sort.order) {
        setSortInfo({
          order: newSortOrder,
          key: newSortBy as AgencyListOrderBy,
        });
      }
    }

    const filtersFromUrl: TableFilter[] = [];
    searchParams.forEach((value, key) => {
      if (!['sortBy', 'sortOrder'].includes(key)) {
        const parsedValue = value.includes(',') ? value.split(',') : value;
        filtersFromUrl.push({ key, value: parsedValue });
      }
    });

    // Check if filters changed by comparing stringified versions
    const currentFiltersStr = JSON.stringify(searchFilters);
    const urlFiltersStr = JSON.stringify(filtersFromUrl);
    if (currentFiltersStr !== urlFiltersStr) {
      setSearchFilters(filtersFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Update URL query params when filters/sorting change
  useEffect(() => {
    const params = new URLSearchParams();

    if (
      sort.key !== AgencyListOrderBy.firstName ||
      sort.order !== SortDirection.ASC
    ) {
      params.set('sortBy', sort.key);
      params.set('sortOrder', sort.order);
    }

    searchFilters.forEach(({ key, value }) => {
      const stringValue = Array.isArray(value)
        ? value.join(',')
        : String(value);
      params.set(key, stringValue);
    });

    const newSearch = params.toString();
    const currentSearch = location.search.replace('?', '');
    if (newSearch !== currentSearch) {
      isUpdatingFromUrl.current = true;
      const basePath = location.pathname;
      history.push({
        pathname: basePath,
        search: newSearch,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, searchFilters]);

  const getAgenciesInput = () => {
    const input: any = {
      direction: sort.order,
      orderBy: sort.key,
      limit: pageSize,
      offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
    };

    searchFilters?.forEach(({ key, value }) => {
      input[key] = value;
    });

    return { input };
  };

  const { data, loading, error, refetch } = useQuery<
    GetAgencies,
    GetAgenciesVariables
  >(GET_AGENCIES, {
    variables: getAgenciesInput(),
    fetchPolicy: 'cache-and-network',
  });

  const { loginAs, loading: isLoggingInAs } = useLoginAs();

  const [selectedBrand, setSelectedBrand] =
    useState<GetAgencies_getAgencies_entities | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const handleViewDetails = (agencyId: string): void => {
    history.push({
      pathname: BRAND_DETAILS.replace(':ownerId', agencyId),
      state: {
        returnToBrands: location.pathname + location.search,
      },
    });
  };

  const handleViewTalentCrm = (agencyId: string): void => {
    history.push({
      pathname: BRAND_TALENT_CRM.replace(':agencyId', agencyId),
      state: {
        returnToBrands: location.pathname + location.search,
      },
    });
  };

  const handleEditBrand = (brand: GetAgencies_getAgencies_entities) => {
    setSelectedBrand(brand);
    setIsEditModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setSelectedBrand(null);
  };

  const handleEditSuccess = () => {
    refetch();
  };

  const handleLoginAs = (brand: GetAgencies_getAgencies_entities): void => {
    Modal.confirm({
      title: 'Login As',
      icon: <ExclamationCircleOutlined />,
      content: `You will be logged out of backoffice and signed in as ${brand.firstName} ${brand.lastName} in Campaign Builder. Continue?`,
      okText: 'Continue',
      cancelText: 'Cancel',
      onOk: () => loginAs(brand.id),
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
    },
    {
      title: 'Company Name',
      dataIndex: ['agencyDetails', 'companyName'],
      key: 'companyName',
      width: 200,
      withSearch: true,
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: AgencyListOrderBy.firstName,
      width: 150,
      sorterType: 'text',
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: AgencyListOrderBy.lastName,
      width: 150,
      sorterType: 'text',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      withSearch: true,
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 150,
    },
    {
      title: 'Talents in CRM',
      dataIndex: 'talentCrmCount',
      key: 'talentCrmCount',
      width: 130,
      render: (value: number | null | undefined) =>
        typeof value === 'number' ? value : '-',
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      fixed: 'right' as const,
      width: 130,
      render: (_: any, record: GetAgencies_getAgencies_entities) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="details"
              onClick={() => handleViewDetails(record.id)}
            >
              Details
            </Menu.Item>
            <Menu.Item key="edit" onClick={() => handleEditBrand(record)}>
              Edit
            </Menu.Item>
            <Menu.Item
              key="talent-crm"
              onClick={() => handleViewTalentCrm(record.id)}
            >
              Talent CRM
            </Menu.Item>
            <Menu.Item key="login-as" onClick={() => handleLoginAs(record)}>
              Login As
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown
            overlay={menu}
            trigger={['click']}
            disabled={loading || isLoggingInAs}
          >
            <Button>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  if (error) {
    return <div>Error loading agencies: {error.message}</div>;
  }

  return (
    <div>
      <Title level={2}>Brands</Title>
      <Table<GetAgencies_getAgencies_entities, AgencyListOrderBy>
        columns={columns}
        data={data?.getAgencies.entities || []}
        scroll={{ x: 1350 }}
        loading={loading}
        total={data?.getAgencies.total}
        defaultPageSize={pageSize}
        defaultCurrent={currentPage}
        setPageAndSize={handleSetPagination}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        sortInfo={sort}
        setSortInfo={setSortInfo}
      />
      <EditBrandModal
        brand={selectedBrand}
        isVisible={isEditModalVisible}
        onClose={handleCloseEditModal}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default BrandsTableComponent;
